import { Controller } from "@hotwired/stimulus";

// Drives step1's results list: polls GET /search/status until the backend
// search is ready, then fetches the same server-rendered fragment the
// mobile flow already uses (Step1Controller#step_1_reload / #step_1_more,
// via <path>/reload and <path>/more/:offset -- unmodified, UA-agnostic
// actions; render_view resolves to the new front/step_1_air_search_results
// partial for a desktop request instead of front_mobile's, see
// lib/render_helper.rb#find_layout_view) and swaps/appends the resulting
// HTML. Replaces the old Vue+Vuex+SearchChannel combo, which itself was
// never a real push/WebSocket channel -- confirmed by reading channel.ts:
// the WebSocket path is entirely commented out, it's a client-driven HTTP
// poll either way, just RPC'd through F-ENGINE's /capi endpoint directly
// instead of through this app's own controller actions.
export default class extends Controller {
  static targets = ["results", "list", "loadMore", "filterForm", "loading", "error", "rulesPanel"];
  static values = { searchId: String, orderBy: String };

  connect() {
    this.basePath = window.location.pathname;
    this.poll();
  }

  disconnect() {
    clearTimeout(this.pollTimer);
  }

  poll() {
    fetch(`/search/status?search_id=${encodeURIComponent(this.searchIdValue)}`, {
      headers: { Accept: "application/json" },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === "ready") {
          this.reload();
        } else if (data.status === "error") {
          this.showError();
        } else {
          this.pollTimer = setTimeout(() => this.poll(), 800);
        }
      })
      .catch(() => {
        this.pollTimer = setTimeout(() => this.poll(), 1500);
      });
  }

  // The filter form's own change handler (see _step_1_filter.html.erb) --
  // debounced 500ms, matching mobile's own #filter_form behaviour exactly
  // (front_mobile/step_1.js.coffee's `filter_timer`), so ticking several
  // airline checkboxes in a row fires one request for the settled
  // selection instead of one per checkbox. Sort tabs (setOrderBy) call
  // reload() directly, bypassing this -- a single deliberate click doesn't
  // need debouncing.
  debouncedReload(event) {
    if (event) event.preventDefault();
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.reload(), 500);
  }

  // Also the filter form's debounced change handler above and the sort
  // tabs (setOrderBy) -- anything that changes which results should show
  // always re-hits the server rather than filtering client-side, matching
  // how filtering already worked before this conversion (a real F-ENGINE
  // re-filter call, not a client-side array filter).
  reload(event) {
    if (event) event.preventDefault();

    const params = this.hasFilterFormTarget ? new URLSearchParams(new FormData(this.filterFormTarget)) : new URLSearchParams();
    // Sort tabs are re-rendered wholesale inside the results fragment on
    // every reload (their active state reads params[:filter_order_by]
    // server-side), unlike the sidebar filter form -- tracked as a
    // controller value instead of a DOM element so a filter checkbox
    // change doesn't reset back to the default sort.
    if (this.orderByValue) params.set("filter_order_by", this.orderByValue);

    fetch(`${this.basePath}/reload?${params.toString()}`, { headers: { Accept: "text/html" } })
      .then((response) => response.text())
      .then((html) => {
        if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden");
        this.resultsTarget.innerHTML = html;
        this.onFirstLoadComplete();
      })
      .catch(() => this.showError());
  }

  // Runs once, after the FIRST reload response ever lands (whether that's
  // the automatic poll-triggered one, or -- in principle -- a filter/sort
  // action that happened to win the race, see enableFilters below).
  //
  // Two things need this:
  //  - The filter sidebar's travel-time slider ships with generous static
  //    bounds (see _step_1_filter.html.erb) because @search.filter_max
  //    isn't known until after the first real fetch. Once it is, tighten
  //    the slider to the real min/max for this search.
  //  - filter_params (Step1Controller) needs airline/airport codes cached
  //    (Rails.cache, written server-side right after eager_load_result_
  //    values on *this same* request) before it can safely build the
  //    "everything selected" alliance/airline/connection_point fallback
  //    any real filter/sort action requires (see its own long comment).
  //    Before that cache exists, the fallback is an *empty* hash, which
  //    F-ENGINE reads as "select nothing" and deletes every result --
  //    confirmed live: touching a filter/slider before this first load
  //    finished reliably zeroed out results. The filter sidebar ships
  //    `disabled` (see _step_1_filter.html.erb) specifically so this race
  //    can't happen -- enabling it only here, after this same request has
  //    guaranteed the cache exists for the next one.
  //  - The alliance/airline/connection_point checkboxes themselves don't
  //    exist in the static sidebar at all yet -- clone them in from the
  //    <template> this same results fragment carries (see its own long
  //    comment), replacing the sidebar's placeholder.
  onFirstLoadComplete() {
    if (this.firstLoadComplete) return;
    this.firstLoadComplete = true;

    if (this.hasFilterFormTarget) {
      this.filterFormTarget.querySelectorAll("input:disabled").forEach((el) => (el.disabled = false));
      const note = this.filterFormTarget.querySelector('[data-role="filterLoadingNote"]');
      if (note) note.classList.add("hidden");

      const template = this.resultsTarget.querySelector('template[data-role="filterExtrasTemplate"]');
      const placeholder = this.filterFormTarget.querySelector('[data-role="filterExtrasPlaceholder"]');
      if (template && placeholder) placeholder.replaceWith(template.content.cloneNode(true));
    }

    const bounds = this.resultsTarget.querySelector("[data-min-traveltime]");
    if (bounds && this.hasFilterFormTarget) {
      const filterController = this.application.getControllerForElementAndIdentifier(this.filterFormTarget, "step1-filter");
      if (filterController) filterController.applyTravelTimeBounds(bounds.dataset.minTraveltime, bounds.dataset.maxTraveltime);
    }
  }

  setOrderBy(event) {
    this.orderByValue = event.currentTarget.dataset.orderBy;
    this.reload();
  }

  loadMore(event) {
    if (event) event.preventDefault();

    const offset = this.hasListTarget ? this.listTarget.children.length : 0;

    fetch(`${this.basePath}/more/${offset}`, { headers: { Accept: "text/html" } })
      .then((response) => (response.ok ? response.text() : ""))
      .then((html) => {
        if (!html) {
          if (this.hasLoadMoreTarget) this.loadMoreTarget.remove();
          return;
        }

        const fragment = document.createElement("div");
        fragment.innerHTML = html;

        const newList = fragment.querySelector('[data-step1-results-target="list"]');
        if (newList && this.hasListTarget) {
          Array.from(newList.children).forEach((card) => this.listTarget.appendChild(card));
        }

        const newLoadMore = fragment.querySelector('[data-step1-results-target="loadMore"]');
        if (this.hasLoadMoreTarget) {
          if (newLoadMore) {
            this.loadMoreTarget.outerHTML = newLoadMore.outerHTML;
          } else {
            this.loadMoreTarget.remove();
          }
        }
      });
  }

  showDetail(event) {
    const uuid = event.currentTarget.dataset.uuid;
    const overlay = this.element.querySelector(`[data-step1-results-target="overlay"][data-overlay-uuid="${uuid}"]`);
    if (!overlay) return;
    overlay.classList.remove("hidden");
    overlay.classList.add("flex");
    document.body.classList.add("modal-open");
  }

  toggleRules(event) {
    event.preventDefault();
    event.stopPropagation();
    const card = event.currentTarget.closest('[data-step1-results-target="card"]');
    const panel = card && card.querySelector('[data-step1-results-target="rulesPanel"]');
    if (panel) panel.classList.toggle("hidden");
  }

  showError() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden");
    if (this.hasErrorTarget) this.errorTarget.classList.remove("hidden");
  }
}
